import clsx from 'clsx'
import Link from 'next/link'
import React from 'react'

import { Icon } from '@wartek-id/icon'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text'
import { Badge } from '@wartek-id/badge'

import { GATrackEvent } from 'utils/googleAnalytics'

import type { Topic } from 'types/microlearning'

interface TopicCardProps extends Omit<Topic, 'headerImage'> {
  topicId: string | number
  isFetchingProgress: boolean
  image?: string
}

const TopicCard = ({
  topicId,
  name,
  totalModule,
  description,
  href,
  image,
  isFetchingProgress = false,
  badge = undefined,
}: Omit<TopicCardProps, 'id' | 'duration'>) => {
  return (
    <div
      data-testid="topic-card"
      className="relative flex flex-col p-3 pl-4 last:mb-0 bg-white rounded overflow-hidden border border-subdued"
    >
      <div className="flex gap-4">
        <div className="flex-auto">
          <div className="mb-1">
            <Link href={href} passHref>
              <Text
                data-topik-id={topicId}
                onClick={() => {
                  GATrackEvent({
                    eventName: 'learning_activity',
                    eventParams: {
                      event_trigger: 'topik_click_from_list',
                      action_type: 'choose_topik',
                      meta: JSON.stringify({
                        topic_id: topicId,
                      }),
                    },
                    sendDefaultParams: true,
                  })
                }}
                className="overlay-link"
                href={href}
                as="a"
                variant="heading-sm"
              >
                {name}
              </Text>
            </Link>
          </div>
          <div className="line-clamp-2 text-ellipsis">
            <Text variant="body-sm" color="subdued">
              {description}
            </Text>
          </div>
          <div className="flex items-center mt-3">
            <Icon
              as="i"
              color="default"
              style={{ fontSize: '16px' }}
              fontSize="small"
            >
              library_books
            </Icon>
            <div className="ml-2 leading-3">
              <Text as="span" variant="helper-bold" color="subdued">
                {totalModule} modul
              </Text>
            </div>
          </div>
        </div>
        <div
          className={clsx(
            'relative flex-grow-0 flex-shrink-0 w-16 h-16 rounded overflow-hidden',
            (!image || !image.length) &&
              'flex items-center justify-center bg-expressive-dark-blue'
          )}
        >
          {!!image && image.length >= 0 ? (
            <img
              className="object-cover object-center w-16 h-16"
              src={image}
              alt={name}
            />
          ) : (
            <Icon
              as="i"
              color="inverse"
              className="block ml-auto"
              fontSize="large"
              title="Topik"
            >
              library_books
            </Icon>
          )}
        </div>
      </div>
      {isFetchingProgress && (
        <Shimmer
          height={16}
          width={140}
          className="rounded-lg mt-3"
          aria-busy={isFetchingProgress ? 'true' : 'false'}
          role="alert"
        />
      )}
      {!isFetchingProgress && !!badge && !!badge.state && (
        <Badge data-testid="badge" className="mt-3" variant={badge?.type}>
          {badge?.text}
        </Badge>
      )}
    </div>
  )
}

export default TopicCard
